/**
 * Twin Kit cart attributes injector.
 *
 * Runs ALONGSIDE resizable-option-bundle.js and engraving-form.liquid.
 * When both component sizes are selected, computes the prorated prices
 * (carry-over algorithm) and writes a consolidated JSON into a hidden
 * input that the Cart Transform Function reads at cart-add time.
 *
 * Inputs (consumed at submit time):
 *   window.twinKitData.kitPrice                  — kit price (decimal string)
 *   window.twinKitData.components[i].retailPrice — retail price of component i
 *   [data-id-input-1], [data-id-input-2]         — variant IDs picked by customer
 *   [data-res-visible-prop-N]                    — resizing selection per component
 *   #engravingText1 / #engravingText2            — engraving text per component
 *                                                  (from engraving-form.liquid popup)
 *
 * Outputs (written as hidden inputs on the product form):
 *   properties[_twin_kit_data]         — JSON for Cart Transform Function
 *                                        Shape:
 *                                          {
 *                                            kitPrice: "385.00",
 *                                            components: [
 *                                              { variantId, retailPrice, price,
 *                                                engraved?: true, resized?: true },
 *                                              ...
 *                                            ]
 *                                          }
 *   properties[_bundle_total_discount] — legacy, no longer read by any function
 *                                        (kept for backwards compat with the
 *                                         retired Discount Function — safe to
 *                                         remove this input from the picker if
 *                                         you want to clean up further).
 *
 * Engraving + resizing are sent as BOOLEAN flags only — the customer-
 * visible text lives on the parent kit line (set by bundle-variant-picker.liquid
 * + engraving-form.liquid in the single-line format) and Shopify propagates
 * it to all expanded children automatically. We just tell the Function which
 * specific component to flag so PostCo can target the right line for return
 * blocking.
 *
 * The existing _bundle_component_1 / _bundle_component_2 line item properties
 * are left intact — the legacy `orders/create` webhook uses them as a
 * fallback path if the Cart Transform Function fails.
 */
(function () {
  document.addEventListener("DOMContentLoaded", init);

  function init() {
    if (!window.twinKitData || !Array.isArray(window.twinKitData.components) || window.twinKitData.components.length === 0) {
      return;
    }

    const container = document.querySelector("[data-bundle-container]");
    if (!container) return;

    const sectionId = container.dataset.sectionId;
    const form =
      document.getElementById(`product-form-${sectionId}`) ||
      container.closest("form");
    if (!form) return;

    // Recompute on every click inside the bundle picker (after Alpine has
    // updated values via the existing handlers) and once more right
    // before form submit, in capture phase so we run before any bubble-
    // phase submit listeners.
    container.addEventListener("click", function () {
      setTimeout(refresh, 0);
    });
    form.addEventListener("submit", refresh, { capture: true });

    // Initial sync in case sizes are pre-selected (edge cases like back-
    // button navigation that restores form state).
    refresh();

    function refresh() {
      const id1 = container.querySelector("[data-id-input-1]")?.value?.trim();
      const id2 = container.querySelector("[data-id-input-2]")?.value?.trim();
      const dataInput = container.querySelector("[data-twin-kit-data-input]");
      const discountInput = container.querySelector(
        "[data-bundle-total-discount-input]"
      );
      if (!dataInput) return;

      if (!id1 || !id2) {
        dataInput.value = "";
        if (discountInput) discountInput.value = "";
        return;
      }

      const kitPrice = parseFloat(window.twinKitData.kitPrice);
      const components = [
        {
          variantId: "gid://shopify/ProductVariant/" + id1,
          retailPrice: parseFloat(window.twinKitData.components[0].retailPrice),
        },
        {
          variantId: "gid://shopify/ProductVariant/" + id2,
          retailPrice: parseFloat(window.twinKitData.components[1].retailPrice),
        },
      ];

      if (isNaN(kitPrice) || components.some((c) => isNaN(c.retailPrice))) {
        dataInput.value = "";
        if (discountInput) discountInput.value = "";
        return;
      }

      const prorated = computeProratedPrices(kitPrice, components);

      // Detect resizing per component. The bundle picker writes a value
      // into `[data-res-visible-prop-N]` only when the customer chose a
      // resizing option for component N. We forward that info in the
      // JSON so the Cart Transform Function can attach a HIDDEN
      // `_resized: "true"` flag to ONLY the expanded component that's
      // actually resized — letting PostCo block returns on the right line.
      //
      // We do NOT route the resizing TEXT itself — the bundle picker
      // already wrote it as a customer-visible property
      // `Resizing (<component>): Original Size: X -> New Size: Y` on the
      // parent kit line, and Shopify propagates that to all expanded
      // children automatically. No need (and we MUST NOT) duplicate it.
      const resizing1 = container.querySelector("[data-res-visible-prop-1]")?.value?.trim() || "";
      const resizing2 = container.querySelector("[data-res-visible-prop-2]")?.value?.trim() || "";

      // Detect engraving per component. The engraving form (separate
      // popup, lives in `engraving-form.liquid`) writes the customer's
      // text into per-section inputs `#engravingText1` and
      // `#engravingText2`. These are populated on Confirm and cleared
      // on Cancel / Remove Engraving, so at submit time they reflect
      // the active engraving selection.
      //
      // Same rationale as resizing: we forward only a boolean per
      // component. The customer-visible `Engraving N: Title: "text" · Font`
      // is set on the parent by the engraving form itself in Javiera's
      // chosen single-line format and propagates to the children. We
      // only need the hidden per-component flag so PostCo can block
      // returns on exactly the engraved line.
      const engr1 = document.querySelector("#engravingText1")?.value?.trim() || "";
      const engr2 = document.querySelector("#engravingText2")?.value?.trim() || "";

      const data = {
        kitPrice: kitPrice.toFixed(2),
        components: prorated.map((p, i) => {
          const entry = {
            variantId: p.variantId,
            retailPrice: p.retailPrice.toFixed(2),
            price: p.proratedPrice.toFixed(2),
          };
          const resizingValue = i === 0 ? resizing1 : resizing2;
          if (resizingValue) {
            // Boolean form preferred — Cart Transform Function only needs
            // to know that component i is resized, not the text itself
            // (text comes from inherited parent property).
            entry.resized = true;
          }
          const engravingValue = i === 0 ? engr1 : engr2;
          if (engravingValue) {
            entry.engraved = true;
          }
          return entry;
        }),
      };
      dataInput.value = JSON.stringify(data);

      if (discountInput) {
        const total =
          prorated.reduce((s, p) => s + p.retailPrice, 0) - kitPrice;
        discountInput.value = total > 0.005 ? total.toFixed(2) : "";
      }
    }
  }

  /**
   * Carry-over prorating: distribute the kit discount across components
   * in integer cents so the sum of prorated prices EXACTLY equals
   * kitPrice (no float drift, no rounding residuals).
   *
   * If kitPrice > sumRetails (anomaly), returns retails as-is — the
   * Function will skip this case.
   */
  function computeProratedPrices(kitPrice, components) {
    const totalRetail = components.reduce((s, c) => s + c.retailPrice, 0);
    const discountTotal = totalRetail - kitPrice;

    if (discountTotal < -0.005 || Math.abs(discountTotal) < 0.005) {
      return components.map((c) => ({
        ...c,
        proratedPrice: c.retailPrice,
      }));
    }

    const totalRetailCents = Math.round(totalRetail * 100);
    const discountTotalCents = Math.round(discountTotal * 100);
    let assigned = 0;

    return components.map((c, i) => {
      const retailCents = Math.round(c.retailPrice * 100);
      const isLast = i === components.length - 1;
      let discCents;
      if (isLast) {
        discCents = discountTotalCents - assigned;
      } else {
        discCents = Math.floor(
          (discountTotalCents * retailCents) / totalRetailCents
        );
        assigned += discCents;
      }
      return { ...c, proratedPrice: (retailCents - discCents) / 100 };
    });
  }
})();